import React, { useState } from 'react';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setSubunitData, setSubunitId } from '@/lib/slices/subunit-slice';
import {
  setActivityDescription,
  setActivityId,
  setActivityName,
  setActivityType,
  setActivityURL,
  resetActivitySlice,
} from '@/lib/slices/activity-slice';
import { setUnitId } from '@/lib/slices/unit-slice';

import MainCourseStudentView from './MainCourseStudentView';
import DocumentHandler from '@/(portal)/student/lesson/screens/DocumentHandler';
import VideoHandler from '@/(portal)/student/lesson/screens/VideoHandler';
import ResourceHandler from '@/(portal)/student/lesson/screens/ResourceHandler';
import Quiz from '@/(portal)/components/quiz/Quiz';
import ActivityNavButtons from '@/(portal)/student/lesson/screens/components/ActivityNavButtons';

import { ActivityData, ActivityType, CourseData, SubunitData } from 'course-types';
import AssignmentHandler from '@/(portal)/student/lesson/screens/AssignmentHandler';

export default function AdminCourseStudentViewManager() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const currentActivity = useAppSelector(() => store.getState().activity);
  const currentSubunit = useAppSelector(() => store.getState().subunit);
  const currentUnitId = useAppSelector(() => store.getState().unit.id);
  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );
  const activityURL = useAppSelector(() => store.getState().activity.url);

  const [activityActive, setActivityActive] = useState<boolean>(false);

  const onActivitySelect = (
    activity: ActivityData,
    subunit?: SubunitData & { id: string },
    unitId?: string,
  ) => {
    if (subunit) {
      dispatch(setSubunitData(subunit));
    } else {
      dispatch(setSubunitId(undefined));
    }
    dispatch(setUnitId(unitId ?? undefined));

    // set activity ID
    dispatch(setActivityId(activity.id));
    dispatch(setActivityType(activity.type));
    dispatch(setActivityName(activity.name));
    dispatch(setActivityDescription(activity.description));

    if (activity.type == 'video' || activity.type == 'resource') {
      dispatch(setActivityURL(activity.url ?? ''));
    }

    setActivityActive(true);
  };

  const deselectActivity = () => {
    setActivityActive(false);
    dispatch(resetActivitySlice());
    dispatch(setSubunitId(undefined));
    dispatch(setUnitId(''));
  };

  const renderContent = (): React.JSX.Element => {
    switch (currentActivity.type as ActivityType) {
      case 'quiz':
        return (
          <Quiz
            quizId={currentActivity.id}
            subunitId={currentSubunit.id}
            unitId={currentUnitId}
            courseId={activeCourseData.id}
            onBack={deselectActivity}
          />
        );
      case 'document':
        return (
          <DocumentHandler
            docId={currentActivity.id}
            subunitId={currentSubunit?.id ?? undefined}
            unitId={currentUnitId}
            courseId={activeCourseData.id}
            activityName={currentActivity.name}
            activityDescription={currentActivity.description}
          />
        );
      case 'video':
        return (
          <VideoHandler
            videoId={currentActivity.id}
            videoURL={activityURL}
            subunitId={currentSubunit.id}
            unitId={currentUnitId}
            courseId={activeCourseData.id}
            activityDescription={currentActivity.description}
          />
        );
      case 'resource':
        return (
          <ResourceHandler
            resourceId={currentActivity.id}
            description={currentActivity.description ?? 'ERROR: NOT FOUND'}
            resourceURL={activityURL}
            subunitId={currentSubunit.id}
            unitId={currentUnitId}
            courseId={activeCourseData.id}
            activityName={currentActivity.name}
            onBack={() => {
              setActivityActive(false);
              dispatch(resetActivitySlice());
              dispatch(setSubunitId(undefined));
              dispatch(setUnitId(''));
            }}
          />
        );
      case 'assignment':
        return (
          <AssignmentHandler
            assignmentId={currentActivity.id}
            courseId={activeCourseData.id}
            unitId={currentUnitId}
            subunitId={currentSubunit.id}
            activityName={currentActivity.name}
            activityDescription={currentActivity.description || ''}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <div className='flex flex-col items-center w-full h-full'>
      {!activityActive && <MainCourseStudentView onActivitySelect={onActivitySelect} />}
      {activityActive && (
        <>
          <div
            className={`w-full ${currentActivity.type !== 'quiz' ? 'h-[calc(100%-68px)]' : 'h-full'}`}
          >
            {renderContent()}
          </div>
          {currentActivity.type !== 'quiz' && <ActivityNavButtons onBack={deselectActivity} />}
        </>
      )}
    </div>
  );
}
